import React, { useEffect, useState } from 'react';
import './ZoneTypePicker.scss';

interface ShowEventDetail {
  zoneId: string;
  suggestedName: string;
  options: string[];
}

// Human wording for the tags the server reports. A tag with no entry here
// still shows - as its raw id - rather than being hidden, because a type the
// admin cannot pick is worse than one with an ugly label.
const LABELS: Record<string, string> = {
  'wildlife-exclusion': 'Exclusion faune',
  'no-pvp': 'Sans PvP',
  'faction-territory': 'Territoire de faction',
  property: 'Périmètre de propriété',
  event: 'Événement',
};

const send = (payload: Record<string, unknown>): void => {
  if ((window as any).skyrimPlatform?.sendMessage) {
    (window as any).skyrimPlatform.sendMessage(JSON.stringify({ source: 'zone-type-picker', ...payload }));
  }
};

// Last step of drawing a zone: the outline is already settled server-side,
// this only asks what it is for. The options come from the server's registered
// consumers, so the list can never offer a type nothing listens to.
const ZoneTypePicker: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [zoneId, setZoneId] = useState('');
  const [options, setOptions] = useState<string[]>([]);
  const [choice, setChoice] = useState('');
  const [name, setName] = useState('');
  const [announce, setAnnounce] = useState(false);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as ShowEventDetail;
      const list = detail?.options ?? [];
      setZoneId(detail?.zoneId ?? '');
      setOptions(list);
      setChoice(list[0] ?? '');
      setName(detail?.suggestedName ?? detail?.zoneId ?? '');
      setAnnounce(false);
      setVisible(true);
    };
    window.addEventListener('skymp5-client:showZoneTypePicker', handler);
    return () => window.removeEventListener('skymp5-client:showZoneTypePicker', handler);
  }, []);

  if (!visible) return null;

  const close = (tag: string) => {
    setVisible(false);
    // An empty tag is the cancel path: the server drops the pending zone
    // rather than saving an untagged one, which would do nothing.
    send({ tag, name: tag ? name : '', announce: tag ? announce : false });
  };

  return (
    <div className="zone-type-picker">
      <div className="zone-type-picker__panel">
        <div className="zone-type-picker__header">
          <div>
            <div className="zone-type-picker__title">À quoi sert cette zone ?</div>
            <div className="zone-type-picker__subtitle">{zoneId}</div>
          </div>
        </div>

        <label className="zone-type-picker__label" htmlFor="zone-type-picker-name">
          Nom affiché
        </label>
        <input
          id="zone-type-picker-name"
          className="zone-type-picker__input"
          type="text"
          value={name}
          maxLength={64}
          onChange={(e) => setName(e.target.value)}
          placeholder="la bordure de Blancherive"
          autoFocus
        />

        <select
          className="zone-type-picker__select"
          value={choice}
          onChange={(e) => setChoice(e.target.value)}
        >
          {options.map((tag) => (
            <option key={tag} value={tag}>
              {LABELS[tag] ?? tag}
            </option>
          ))}
        </select>

        <label className="zone-type-picker__check">
          <input type="checkbox" checked={announce} onChange={(e) => setAnnounce(e.target.checked)} />
          <span>
            Annoncer l&apos;entrée et la sortie
            {announce && name ? ` - « Vous entrez dans ${name}. »` : ''}
          </span>
        </label>

        <div className="zone-type-picker__actions">
          <button
            type="button"
            className="zone-type-picker__button zone-type-picker__button--cancel"
            onClick={() => close('')}
          >
            Abandonner
          </button>
          <button
            type="button"
            className="zone-type-picker__button zone-type-picker__button--confirm"
            onClick={() => close(choice)}
            disabled={!choice}
          >
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
};

export default ZoneTypePicker;
